import { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import { formatDeadlineCountdown } from '../utility/deadlineUtils'

/**
 * Custom hook that provides a live-updating deadline countdown.
 *
 * Timer resolution adapts to urgency:
 *  - > 24h away  → updates every 60s
 *  - 1h – 24h    → updates every 60s
 *  - < 1h        → updates every 1s
 *
 * @param {Date|string|null} deadlineDate - The deadline timestamp
 * @returns {{ text: string, isWarning: boolean, isExpired: boolean } | null}
 */
export function useDeadlineCountdown(deadlineDate) {
    const [countdown, setCountdown] = useState(() => formatDeadlineCountdown(deadlineDate))
    const intervalRef = useRef(null)

    useEffect(() => {
        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }

        if (!deadlineDate) {
            setCountdown(null)
            return
        }

        // Initial calculation
        const initial = formatDeadlineCountdown(deadlineDate)
        setCountdown(initial)

        // Don't start a timer if already expired
        if (initial?.isExpired) return

        const getInterval = () => {
            const diffMs = moment(deadlineDate).diff(moment())
            if (diffMs <= 0) return null // expired
            const totalMinutes = Math.floor(diffMs / 60000)
            // < 1 hour → update every second
            if (totalMinutes < 60) return 1000
            // otherwise → update every minute
            return 60000
        }

        const tick = () => {
            const result = formatDeadlineCountdown(deadlineDate)
            setCountdown(result)

            // If expired, stop the interval
            if (result?.isExpired) {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current)
                    intervalRef.current = null
                }
                return
            }

            // Check if we need to switch resolution (minute → second)
            const needed = getInterval()
            if (needed !== null && needed !== currentInterval) {
                clearInterval(intervalRef.current)
                currentInterval = needed
                intervalRef.current = setInterval(tick, currentInterval)
            }
        }

        let currentInterval = getInterval()
        if (currentInterval !== null) {
            intervalRef.current = setInterval(tick, currentInterval)
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [deadlineDate])

    return countdown
}
